/**
 * ReportPaywall — the unlock card shown at the bottom of the locked report
 * preview (ReportView in `locked` mode). Unlike ReportUpsellCard, which sits
 * on the quiz result page and encodes live `Answers`, this one already holds
 * the encoded blob (the preview page decodes nothing — it forwards the same
 * snapshot the buyer will pay for), so it forwards the blob straight to the
 * checkout. This guarantees the purchased report is byte-identical to the
 * preview the buyer just scrolled through.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { track } from '@/features/analytics';
import { createReportCheckout } from './api';

export interface ReportPaywallProps {
  /** encodeAnswersForUrl() blob — the exact snapshot being previewed. */
  answersBlob: string;
  lang: 'en' | 'ru';
}

export function ReportPaywall({ answersBlob, lang }: ReportPaywallProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buy = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    track('report_checkout_clicked', { lang, surface: 'preview' });
    try {
      const res = await createReportCheckout({ answers: answersBlob, lang });
      const target = res.checkout_url ?? res.report_url;
      if (target) {
        window.location.assign(target);
        return;
      }
      setError(t('report.upsell.error', { defaultValue: 'Checkout failed — try again.' }));
    } catch {
      setError(t('report.upsell.error', { defaultValue: 'Checkout failed — try again.' }));
    }
    setLoading(false);
  };

  const unlocks = [
    t('report.locked.unlock1', {
      defaultValue: 'All three countries in full — not just your #1',
    }),
    t('report.locked.unlock2', {
      defaultValue: 'Exact visa pathway + monthly income floor for your passport',
    }),
    t('report.locked.unlock3', {
      defaultValue: 'Tax regime, property & car costs — the first-year budget in numbers',
    }),
    t('report.locked.unlock4', {
      defaultValue: 'A 12-month action plan per country + side-by-side comparison',
    }),
    t('report.locked.unlock5', {
      defaultValue: 'A permanent link, PDF-ready for printing',
    }),
  ];

  return (
    <section
      data-testid="report-paywall"
      className="relative z-10 -mt-6 rounded-xl border-2 border-terra-600 bg-paper-2 p-6 shadow-lg"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-terra-600 text-white">
          <Lock className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-xl italic text-ink-900">
            {t('report.locked.title', {
              defaultValue: 'Unlock the full report',
            })}
          </h2>
          <p className="mt-1 text-sm text-ink-600">
            {t('report.locked.subtitle', {
              defaultValue:
                'You just saw the shape of your #1. The numbers that turn “where” into “how” — and the other two countries — are in the full document. A relocation consultant charges €100+ for one hour; this is the prepared version of that hour.',
            })}
          </p>
        </div>
      </div>

      <ul className="mt-4 space-y-1.5 pl-12 text-sm text-ink-700">
        {unlocks.map((u, i) => (
          <li key={i} className="list-disc">{u}</li>
        ))}
      </ul>

      <div className="mt-5 flex flex-wrap items-center gap-3 pl-12">
        <Button onClick={buy} disabled={loading} data-testid="report-paywall-buy">
          {loading
            ? t('report.upsell.loading', { defaultValue: 'Opening checkout…' })
            : t('report.upsell.cta', { defaultValue: 'Get my report — €29' })}
        </Button>
        <span className="font-mono text-[11px] text-ink-400">
          {t('report.upsell.note', {
            defaultValue: 'One-time payment · instant access · permanent link',
          })}
        </span>
      </div>
      {error && (
        <p className="mt-2 pl-12 text-xs text-red-700" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
